import React, { useState, useEffect } from "react";
import { Lock, X, CreditCard } from "lucide-react";
import { login, register, oauthLogin } from "../auth";
import { createPayment, apiFetch } from "../api";
import { openPaymentWidget } from "../yookassa";

export default function PaymentModal({ onClose, onPay, onPolicy, onOffer, user, onAuth, planUuid, onSuccess }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [authError, setAuthError] = useState("");
  const [price, setPrice] = useState(1000);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/config');
        if (res.ok) {
          const data = await res.json();
          if (typeof data.planPrice === 'number') setPrice(data.planPrice);
        }
      } catch {}
    })();
  }, []);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 mb-3">
              <Lock className="h-5 w-5" />
            </div>
            <h2 className="text-xl font-semibold leading-tight">
              Доступ к полному бизнес‑плану
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ul className="mt-3 space-y-1 text-sm text-slate-700">
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> Готовый
            черновик под соцконтракт
          </li>
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            Редактирование и экспорт
          </li>
          <li className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> +1 повтор
            генерации
          </li>
        </ul>

        {!user && (
          <>
            <div className="mt-4 space-y-3">
              <p className="font-semibold">Авторизация или регистрация:</p>
              <input
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailValid(emailRegex.test(e.target.value));
                }}
                placeholder="E‑mail"
                className={`w-full rounded-lg border px-3 py-2 text-[15px] ${
                  email && !emailValid ? 'border-red-500' : ''
                }`}
              />
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder="Пароль"
                className="w-full rounded-lg border px-3 py-2 text-[15px]"
              />
              {authError && (
                <div className="text-sm text-red-600">
                  {authError}
                </div>
              )}
              {authError && (
                <div className="text-right text-sm">
                  <button
                    type="button"
                    className="text-blue-600 underline hover:text-blue-700"
                    onClick={() => {
                      try {
                        window.open('/forgot-password', '_blank', 'noopener,noreferrer');
                      } catch (_) {}
                    }}
                  >
                    Забыли пароль?
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={async () => {
                const u = await oauthLogin();
                if (u) onAuth(u);
              }}
              className="mt-3 inline-flex w-full h-11 items-center justify-center rounded-lg border px-5 text-sm"
              style={{ display: 'none' }}
            >
              или Войти через госуслуги
            </button>
          </>
        )}

        <div className="my-4 text-center text-2xl font-semibold">{new Intl.NumberFormat('ru-RU').format(price)} ₽</div>

        <button
              onClick={async () => {
                if (typeof window !== "undefined" && typeof window.ym === "function") {
                  window.ym(104041659, "reachGoal", "PAY_IN_EDITOR");
                }
                setBusy(true);
                try {
                  let currentUser = user;

                  if (!currentUser) {
                    setAuthError("");
                    if (!email || !pass || !emailValid) return; // защита от пустых и некорректных полей
                    if (!emailRegex.test(email)) return;
                    const loginResult = await login(email, pass);
                    if (loginResult && loginResult.error === 'locked') {
                      const minutes = Math.max(1, Number(loginResult.minutesLeft || 0));
                      setAuthError(`Вы исчерпали все попытки входа, пожалуйста подождите ${minutes} минут или нажмите "Забыли пароль?"`);
                      // Пользователь заблокирован: не пробуем регистрировать
                      return;
                    }
                    if (loginResult && loginResult.user) {
                      currentUser = loginResult.user;
                    } else {
                      // Логин не удался — пробуем зарегистрировать нового пользователя
                      const reg = await register(email, pass);
                      if (reg && !reg.error && reg.user) {
                        currentUser = reg.user;
                      } else {
                        // Если пользователь уже существует, значит пароль неверный
                        if (reg && reg.error === 409) {
                          setAuthError('Неверный логин или пароль');
                        } else {
                          setAuthError('Не удалось выполнить вход. Попробуйте позже');
                        }
                        return;
                      }
                    }
                    onAuth?.(currentUser); // обновим наверх, но ниже используем именно currentUser
                  }

                // важно: использовать currentUser.id, а не user?.id
                const payment = await createPayment(planUuid, price);

                // confirmation_token может быть как в camel, так и в snake / внутри confirmation
                const token =
                    payment?.confirmation?.confirmation_token ??
                    payment?.confirmationToken ??
                    payment?.confirmation_token;

                if (!token) {
                  console.error('Нет confirmation_token в ответе:', payment);
                  return;
                }

                onPay?.();
                // передаём id плана в колбэк успеха оплаты
                openPaymentWidget(token, () => onSuccess?.(planUuid));
              } catch (e) {
                console.error(e);
              } finally {
                setBusy(false);
              }
            }}
              disabled={busy || (!user && (!email || !pass || !emailValid))}
              className="inline-flex w-full h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white font-semibold shadow hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Обработка…" : "Оплатить и получить доступ"}
          </button>


        <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-500">
          <CreditCard className="h-4 w-4" /> Оплата защищена
        </div>
        <div className="mt-1 text-[12px] text-slate-500">
          Нажимая на кнопку "Оплатить и получить доступ" Вы соглашаетесь с{' '}
          <button onClick={onPolicy} className="underline">
            Политикой конфиденциальности
          </button>{' '}
          и{' '}
          <button onClick={onOffer} className="underline">
            Офертой
          </button>
        </div>
        <div className="mt-4 rounded-lg bg-slate-100 px-3 py-2 text-[12px] text-slate-600">
          Генератор соц-план.рф может допускать ошибки. Проверьте важную информацию.{' '}
          <button
            type="button"
            onClick={() => setShowDisclaimer(true)}
            className="underline"
          >
            См. здесь
          </button>
        </div>
      </div>
      {showDisclaimer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold">Важная информация</h3>
              <button
                onClick={() => setShowDisclaimer(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Сайт соц-план.рф генерирует бизнес план с помощью искусственного интелекта, который может допускать ошибки.
              Бизнес план созданный с помощью сайта не является руководством к действию и ответственность за реализацию бизнес
              плана и его использование в любых целях берет на себя сам пользователь. Мы не гарантируем что государственный
              орган примет сгенерированный бизнес-план и одобрит его, на это влияет много факторов не зависящих от нас. Пользователь
              должен отредактировать сгенерированный бизенс план перед использованием. Подробная информация об условиях
              обслуживания содержится на странице{' '}
              <a
                href="https://соц-план.рф/offer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                https://соц-план.рф/offer
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
